<template>
    <q-drawer 
        bordered
        side="right" 
        :breakpoint="800"
        :model-value="open"
        @update:model-value="(val) => { $emit('update:open', val); }"
        :behavior="$q.screen.width > 800 ? 'desktop' : 'mobile'"
        :class="{ 'bg-grey-1': !$q.dark.isActive }"
        @before-show="BulletinStore.fetch()"
    >
        <template v-if="BulletinStore.count ?? false">
            <q-scroll-area style="height: calc(100% - 50px);">
                <q-list separator v-if="BulletinStore.data.length">
                    <q-item 
                        clickable
                        @click="viewBulletin(bulletin.id)"
                        v-for="(bulletin, i) in BulletinStore.data"
                        :class="{
                            'q-pa-none q-pr-sm': true,
                            'bg-grey-1': i % 2 === 0 && !$q.dark.isActive,
                            'bg-black': $q.dark.isActive,
                        }"
                    >
                        <q-item-section class="q-pr-sm" side>
                            <q-separator vertical :color="bulletin.color ?? 'transparent'" style="width: 5px;height: 100%;" />
                        </q-item-section>
                        <q-item-section class="q-pa-sm" :class="{'text-italic text-weight-thin': bulletin.is_read == 1 }" style="justify-content: flex-start;overflow: hidden;">
                            <q-item-label>
                                {{ bulletin.title }}
                            </q-item-label>
                            <q-item-label caption style="font-size: 0.65rem;">
                                <q-icon name="mdi-account" size="0.75rem" class="q-mr-xs" />
                                {{ bulletin.created_by.name }}
                            </q-item-label>
                            <q-item-label caption style="font-size: 0.65rem;">
                                <q-icon name="mdi-clock" size="0.75rem" class="q-mr-xs" />
                                {{ formatDistance(bulletin.created_at) }}
                            </q-item-label>
                            <q-item-label v-if="bulletin.is_read == 0" class="text-center" caption style="font-size: 0.65rem;font-style: italic;">
                                Unread
                            </q-item-label>
                        </q-item-section>
                        <q-item-section class="q-py-sm" side>
                            <q-item-label caption style="font-size: 0.65rem;display: flex; flex-direction: row;justify-content: center;align-items: center;">
                                {{ numberToShorthand(bulletin.views_count) }}
                                <q-icon name="mdi-eye" size="0.75rem" class="q-ml-sm" />
                            </q-item-label>
                        </q-item-section>
                        <q-item-section v-if="false && crud" class="q-py-sm" side>
                            <q-fab
                                padding="3px"
                                icon="mdi-dots-vertical"
                                direction="left"
                                @click.stop.prevent
                                :color="!$q.dark.isActive ? 'white' : 'grey-10'"
                                :text-color="!$q.dark.isActive ? 'grey' : 'white'"
                            >
                                <q-fab-action
                                    color="primary"
                                    icon="mdi-pencil"
                                    padding="xs"
                                    @click="editBulletin(bulletin.id)"
                                />
                                <q-fab-action
                                    color="negative"
                                    icon="mdi-delete"
                                    padding="xs"
                                    @click="deleteBulletin(bulletin.id)"
                                />
                            </q-fab>
                        </q-item-section>
                        <q-tooltip v-if="!crud">
                            Read Post
                        </q-tooltip>
                    </q-item>
                </q-list>
                <div v-else class="text-center text-grey-5 q-py-lg" style="font-size: 1rem;">
                    <q-icon name="mdi-bullhorn" size="1.2rem" style="margin-top: -5px;" /> No Bulletins Found
                    <div class="text-grey-4" style="font-size: 0.8rem">No news is good news <q-icon name="mdi-emoticon-cool-outline" /></div>
                </div>
            </q-scroll-area>

            <template v-if="crud">
                <q-separator style="margin-top: auto;" />

                <q-item class="q-pa-none text-center">
                    <q-item-section class="no-wrap q-pa-none">
                        <q-btn
                            flat
                            no-caps
                            label="Make New Bulletin"
                            color="primary"
                            style="height: 48px;"
                            @click="createBulletin()"
                        />
                    </q-item-section>
                </q-item>
            </template>
        </template>

        <q-inner-loading :showing="BulletinStore.loading">
            <q-spinner size="2rem" />
        </q-inner-loading>
    </q-drawer>
    
    <q-dialog
        v-if="crud"
        full-width
        v-model="messageDialog.open"
    >
        <q-card 
            :flat="$q.dark.isActive"
            :square="$q.dark.isActive"
            :bordered="$q.dark.isActive"
            style="max-width: 98vw;width: 700px;"
        >
            <q-toolbar 
                :class="{
                    'bg-primary text-white': !$q.dark.isActive,
                }"
            >
                <q-toolbar-title>
                    {{ messageDialog.editing ? (`Editing "${messageDialog.bulletin.title}"`) : 'Creating New Bulletin Message' }}
                </q-toolbar-title>

                <q-btn
                    flat
                    round
                    dense
                    icon="mdi-close"
                    v-close-popup
                />
            </q-toolbar>

            <q-separator />

            <q-card-section class="q-pa-md">
                <q-form
                    class="q-gutter-sm"
                    @submit.prevent="messageDialog.editing ? updateBulletin() : storeBulletin()"
                >
                    <q-input
                        counter
                        clearable
                        lazy-rules
                        label="Title"
                        v-model="messageDialog.form.title"
                        maxlength="250"
                        :rules="[
                            val => val.length > 0 || 'Title is required',
                            val => val.length <= 250 || 'Title can be no more than 250 characters long'
                        ]"
                    />
                    
                    <q-select
                        dense
                        v-model="messageDialog.form.color"
                        :options="colorOptions"
                        label="Highlight Color"
                        emit-value
                        map-options
                        class="q-mb-md"
                    />
                    
                    <markdown-editor
                        v-model="messageDialog.form.content"
                        class="q-mb-md"
                    />

                    <div>
                        <q-btn label="Submit" type="submit" color="positive" @click="messageDialog.editing ? updateBulletin() : createBulletin()" />
                        <q-btn label="Reset" type="reset" color="primary" flat class="q-ml-sm" />
                    </div>
                </q-form>
            </q-card-section>
        </q-card>
    </q-dialog>
    
    <q-dialog v-model="viewMessageDialog.open">
        <q-card 
            :flat="$q.dark.isActive"
            :square="$q.dark.isActive"
            :bordered="$q.dark.isActive"
            style="max-width: 98vw;width: 700px;"
        >
            <q-toolbar :class="{'bg-primary text-white': !$q.dark.isActive}">
                <q-toolbar-title>
                    {{ viewMessageDialog.bulletin.title }}
                </q-toolbar-title>

                <q-btn
                    flat
                    round
                    dense
                    icon="mdi-close"
                    v-close-popup
                />
            </q-toolbar>

            <q-separator />

            <q-card-section class="q-pa-md">
                <markdown-editor
                    v-model="viewMessageDialog.bulletin.content"
                    class="q-mb-md"
                    output-only
                />

                <q-inner-loading :showing="viewMessageDialog.loading">
                    <q-spinner size="2rem" />
                </q-inner-loading>
            </q-card-section>

            <q-separator />

            <q-card-actions align="right">
                <q-btn label="Delete" color="negative" @click="deleteBulletin(viewMessageDialog.bulletin.id)" />
                <q-btn label="Edit" color="primary" @click="editBulletin(viewMessageDialog.bulletin.id)" />
                <q-btn flat v-close-popup label="Close" />
            </q-card-actions>
        </q-card>
    </q-dialog>
</template>

<script>
import MarkdownEditor from '@/Components/markdownEditor.vue';

import { useBulletinStore } from '@/Stores/BulletinStore.js';

export default {
    name: 'BulletinSidebar',
    components: {
        MarkdownEditor,
    },
    props: {
        open: Boolean,
    },
    setup() {
        const BulletinStore = useBulletinStore();

        return {
            BulletinStore,
        };
    },
    data() {
        return {
            loading: true,
            bulletins: [],
            pagination: {
                page: 1,
                rows: 5,
                total: 0,
            },
            messageDialog: {
                open: false,
                loading: false,
                editing: false,
                form: {
                    id: 0,
                    title: '',
                    message: '',
                    color: 'transparent',
                },
                bulletin: {
                    id: 0,
                    title: '',
                    color: '',
                    message: '',
                    created_at: '',
                    created_by: '',
                },
            },
            viewMessageDialog: {
                open: false,
                loading: false,
                bulletin: {
                    id: 0,
                    color: '',
                    title: '',
                    content: '',
                    created_at: '',
                    created_by: '',
                },
            },
            colorOptions: [
                {
                    label: 'None',
                    value: 'transparent',
                },
                {
                    label: 'Negative',
                    value: 'negative',
                },
                {
                    label: 'Positive',
                    value: 'positive',
                },
                {
                    label: 'Warning',
                    value: 'warning',
                },
                {
                    label: 'Info',
                    value: 'info',
                },
                {
                    label: 'Primary',
                    value: 'primary',
                },
                {
                    label: 'Secondary',
                    value: 'secondary',
                },
                {
                    label: 'Dark',
                    value: 'dark',
                },
                {
                    label: 'Light',
                    value: 'light',
                },
            ],
        }
    },
    methods: {
        viewBulletin(id) {
            this.viewMessageDialog.loading = true;

            this.axios.get(route('bulletin.show', { id }))
                .then(({data}) => {
                    if (data.success) {
                        this.BulletinStore.fetch();
                        this.viewMessageDialog.bulletin = data.data;
                        this.viewMessageDialog.open = true;
                    }
                })
                .catch(error => {
                    console.error(error);
                })
                .finally(() => {
                    this.viewMessageDialog.loading = false;
                })
        },
        editBulletin(id) {
            if (!this.crud) return;

            this.messageDialog.loading = true;

            if (this.viewMessageDialog.open) {
                this.viewMessageDialog.open = false;
            }

            this.axios.get(route('bulletin.show', { id }))
                .then(({data}) => {
                    if (data.success) {
                        this.messageDialog.bulletin = data.data;
                        this.messageDialog.form = data.data;
                        this.messageDialog.editing = true;
                        this.messageDialog.open = true;
                    }
                })
                .catch(error => {
                    console.error(error);
                })
                .finally(() => {
                    this.messageDialog.loading = false;
                });
        },
        createBulletin() {
            this.messageDialog.editing = false;
            this.messageDialog.open = true;
        },
        updateBulletin() {
            if (!this.crud) return;

            this.loading = true;

            this.axios.put(route('bulletin.update', { 
                id: this.messageDialog.bulletin.id 
            }),
                this.messageDialog.form
            )
                .then(({data}) => {
                    if (data.success) {
                        this.BulletinStore.fetch();
                        this.messageDialog.open = false;

                        this.$q.notify({
                            message: 'Bulletin updated successfully',
                            color: 'positive',
                            icon: 'mdi-check',
                        });
                    }
                })
                .catch(error => {
                    this.loading = false;

                    console.error(error);

                    this.$q.notify({
                        message: 'Error updating bulletin!',
                        color: 'negative',
                        icon: 'mdi-close-circle',
                    });
                });
        },
        deleteBulletin(id) {
            if (!this.crud) return;

            if (!confirm('Are you sure you want to delete this bulletin?')) return;

            this.axios.delete(route('bulletin.destroy', { id }))
                .then(({data}) => {
                    if (data.success) {
                        if (this.viewMessageDialog.open) {
                            this.viewMessageDialog.open = false;
                        }

                        this.BulletinStore.fetch();
                        this.messageDialog.open = false;

                        this.$q.notify({
                            message: 'Bulletin deleted successfully',
                            color: 'positive',
                            icon: 'mdi-check',
                        });
                    }
                })
                .catch(error => {
                    this.loading = false;

                    console.error(error);

                    this.$q.notify({
                        message: 'Error deleting bulletin!',
                        color: 'negative',
                        icon: 'mdi-close-circle',
                    });
                });
        },
        storeBulletin() {
            if (!this.crud) return;

            this.loading = true;
            
            this.axios.post(route('bulletin.store'), this.messageDialog.form)
                .then(({data}) => {
                    if (data.success) {
                        this.BulletinStore.fetch();
                        this.messageDialog.open = false;

                        this.$q.notify({
                            message: 'Bulletin created successfully',
                            color: 'positive',
                            icon: 'mdi-check',
                        });
                    }
                })
                .catch(error => {
                    this.loading = false;

                    console.error(error);

                    this.$q.notify({
                        message: 'Error creating bulletin!',
                        color: 'negative',
                        icon: 'mdi-close-circle',
                    });
                });
        },
        numberToShorthand(num) {
            switch (true) {
                case (num >= 1e9):
                    return +(num / 1e9).toFixed(1) + 'B';
                case (num >= 1e6):
                    return +(num / 1e6).toFixed(1) + 'M';
                case (num >= 1e3):
                    return +(num / 1e3).toFixed(1) + 'K';
                default:
                    return num;
            }
        },
    },
    watch: {
        'messageDialog.open': function (val) {
            if (!val) {
                this.messageDialog.editing = false;
                this.messageDialog.bulletin = {
                    id: 0,
                    title: '',
                    color: '',
                    content: '',
                    created_at: '',
                    created_by: '',
                };
                this.messageDialog.form = {
                    id: 0,
                    title: '',
                    message: '',
                    color: 'transparent',
                };
            }
        },
    },
    computed: {
        crud() {
            return this.isAdmin() || this.isManager();
        },
        showing() {
            const { from, to, pagination } = this.paginationLabel(
                this.pagination,
            );

            return `${from}-${to} of ${pagination.total}`;
        },
        paginated() {
            const labelObj = this.paginationLabel(this.pagination);

            return {
                ...labelObj,
                firstPage: () => this.pagination.page = 1,
                prevPage: () => this.pagination.page--,
                nextPage: () => this.pagination.page++,
                lastPage: () => this.pagination.page = labelObj.pagesNumber,
                showing: (showingTxt = false, pluralize = true) => {
                    let txt = `Showing ${labelObj.from}-${labelObj.to} of ${labelObj.pagination.total} `;

                    if (showingTxt) {
                        txt += showingTxt;

                        if (pluralize) {
                            txt += labelObj.pagination.total != 1 ? 's' : '';
                        }
                    }

                    return txt;
                }
            };
        },
    },
}
</script>